// Category taxonomy v2 — full segment > category > subcategory tree.
// IDs are stable slugs used as the source of truth. Priorities (P1-P4) are
// internal-only and used purely to sort items so the most relevant options
// surface first. They are never displayed in the UI.

export type Priority = 1 | 2 | 3 | 4;

export interface SubcategoryNode {
  id: string;
  name: string;
  priority: Priority;
}

export interface CategoryNode {
  id: string;
  name: string;
  emoji: string;
  priority: Priority;
  hint?: string;
  subcategories: SubcategoryNode[];
}

export interface SegmentNode {
  id: string;
  name: string;
  emoji: string;
  hint: string;
  categories: CategoryNode[];
}

export const segments: SegmentNode[] = [
  {
    id: "infw",
    name: "Influencerka",
    emoji: "👩",
    hint: "Tworzysz codzienny content na własny profil",
    categories: [
      {
        id: "infw_beauty",
        name: "Uroda & Beauty",
        emoji: "💄",
        priority: 1,
        subcategories: [
          { id: "infw_beauty_makeup", name: "Makijaż & beauty looks", priority: 1 },
          { id: "infw_beauty_skincare", name: "Pielęgnacja twarzy & skincare", priority: 1 },
          { id: "infw_beauty_aesthetic", name: "Medycyna estetyczna", priority: 1 },
          { id: "infw_beauty_nails", name: "Manicure & paznokcie", priority: 1 },
          { id: "infw_beauty_hair", name: "Pielęgnacja włosów", priority: 2 },
          { id: "infw_beauty_brows", name: "Brwi & rzęsy", priority: 2 },
          { id: "infw_beauty_pmu", name: "Makijaż permanentny & zabiegi", priority: 2 },
          { id: "infw_beauty_perfume", name: "Perfumy & zapachy", priority: 3 },
          { id: "infw_beauty_eco", name: "Naturalna & eko uroda", priority: 3 },
        ],
      },
      {
        id: "infw_lifestyle",
        name: "Lifestyle & Rozrywka",
        emoji: "✨",
        priority: 1,
        subcategories: [
          { id: "infw_lifestyle_vlog", name: "Vlog & codzienność", priority: 1 },
          { id: "infw_lifestyle_routine", name: "Morning & evening routine", priority: 1 },
          { id: "infw_lifestyle_humor", name: "Humor & relatable content", priority: 1 },
          { id: "infw_lifestyle_series", name: "Seriale & filmy", priority: 2 },
          { id: "infw_lifestyle_planner", name: "Planowanie & bullet journal", priority: 2 },
          { id: "infw_lifestyle_astro", name: "Astrologia & tarot", priority: 3 },
        ],
      },
      {
        id: "infw_health",
        name: "Zdrowie & Fitness",
        emoji: "💪",
        priority: 1,
        subcategories: [
          { id: "infw_health_strength", name: "Trening siłowy", priority: 1 },
          { id: "infw_health_pilates", name: "Pilates & yoga", priority: 1 },
          { id: "infw_health_diet", name: "Odchudzanie & dieta", priority: 1 },
          { id: "infw_health_cardio", name: "Bieganie & cardio", priority: 2 },
          { id: "infw_health_mental", name: "Zdrowie psychiczne", priority: 2 },
          { id: "infw_health_cycle", name: "Cykl & hormony", priority: 3 },
        ],
      },
      {
        id: "infw_fashion",
        name: "Moda & Styl",
        emoji: "👗",
        priority: 1,
        subcategories: [
          { id: "infw_fashion_outfits", name: "Codzienne stylizacje & outfity", priority: 1 },
          { id: "infw_fashion_haul", name: "Haule & recenzje zakupów", priority: 1 },
          { id: "infw_fashion_plus", name: "Moda plus size", priority: 1 },
          { id: "infw_fashion_street", name: "Streetwear & oversized", priority: 2 },
          { id: "infw_fashion_business", name: "Biznesowy look", priority: 2 },
          { id: "infw_fashion_vintage", name: "Second-hand & vintage", priority: 2 },
          { id: "infw_fashion_shoes", name: "Buty & torebki", priority: 2 },
          { id: "infw_fashion_jewelry", name: "Biżuteria & akcesoria", priority: 3 },
          { id: "infw_fashion_capsule", name: "Capsule wardrobe", priority: 3 },
        ],
      },
      {
        id: "infw_food",
        name: "Jedzenie & Gastronomia",
        emoji: "🍳",
        priority: 2,
        subcategories: [
          { id: "infw_food_recipes", name: "Przepisy & gotowanie", priority: 1 },
          { id: "infw_food_fit", name: "Fit przepisy", priority: 1 },
          { id: "infw_food_sweet", name: "Słodkie wypieki", priority: 2 },
          { id: "infw_food_restaurants", name: "Restauracje & food tour", priority: 2 },
          { id: "infw_food_vegan", name: "Kuchnia wegańska", priority: 3 },
        ],
      },
      {
        id: "infw_family",
        name: "Rodzina & Macierzyństwo",
        emoji: "👶",
        priority: 2,
        subcategories: [
          { id: "infw_family_motherhood", name: "Macierzyństwo & niemowlęctwo", priority: 1 },
          { id: "infw_family_parenting", name: "Parenting & wychowanie dzieci", priority: 1 },
          { id: "infw_family_pregnancy", name: "Ciąża & przygotowanie do porodu", priority: 2 },
          { id: "infw_family_special", name: "Dzieci z wyzwaniami (ADHD, autyzm)", priority: 2 },
          { id: "infw_family_partner", name: "Życie z partnerem", priority: 2 },
          { id: "infw_family_wedding", name: "Planowanie ślubu", priority: 3 },
          { id: "infw_family_maternity_fashion", name: "Moda ciążowa & po porodzie", priority: 3 },
        ],
      },
      {
        id: "infw_relations",
        name: "Relacje & Dating",
        emoji: "💕",
        priority: 2,
        subcategories: [
          { id: "infw_relations_toxic", name: "Toksyczne relacje", priority: 1 },
          { id: "infw_relations_communication", name: "Związki & komunikacja", priority: 1 },
          { id: "infw_relations_single", name: "Single life", priority: 2 },
          { id: "infw_relations_dating", name: "Randkowanie & dating apps", priority: 2 },
          { id: "infw_relations_divorce", name: "Rozwód & rozstanie", priority: 2 },
          { id: "infw_relations_friendship", name: "Przyjaźnie", priority: 3 },
        ],
      },
      {
        id: "infw_finance",
        name: "Finanse & Biznes",
        emoji: "💼",
        priority: 3,
        subcategories: [
          { id: "infw_finance_budget", name: "Budżetowanie & oszczędzanie", priority: 1 },
          { id: "infw_finance_branding", name: "Social media & personal branding", priority: 1 },
          { id: "infw_finance_sidehustle", name: "Zarabianie online & side hustle", priority: 2 },
          { id: "infw_finance_invest", name: "Inwestowanie", priority: 2 },
          { id: "infw_finance_career", name: "Kariera & rozwój zawodowy", priority: 2 },
          { id: "infw_finance_freelance", name: "Freelancing", priority: 3 },
        ],
      },
      {
        id: "infw_home",
        name: "Dom & Wnętrza",
        emoji: "🏡",
        priority: 3,
        subcategories: [
          { id: "infw_home_cleaning", name: "Sprzątanie & organizacja", priority: 1 },
          { id: "infw_home_design", name: "Urządzanie & aranżacja wnętrz", priority: 2 },
          { id: "infw_home_first", name: "Wynajem & pierwsze mieszkanie", priority: 2 },
          { id: "infw_home_budget", name: "Wystrój na budżet", priority: 2 },
          { id: "infw_home_diy", name: "Dekoracje DIY", priority: 3 },
          { id: "infw_home_plants", name: "Rośliny domowe", priority: 3 },
        ],
      },
      {
        id: "infw_education",
        name: "Edukacja & Rozwój",
        emoji: "🧠",
        priority: 3,
        subcategories: [
          { id: "infw_education_self", name: "Self-development & motywacja", priority: 1 },
          { id: "infw_education_productivity", name: "Produktywność & organizacja czasu", priority: 2 },
          { id: "infw_education_psychology", name: "Psychologia praktyczna", priority: 2 },
          { id: "infw_education_mindfulness", name: "Mindfulness & medytacja", priority: 2 },
          { id: "infw_education_languages", name: "Nauka języków", priority: 3 },
          { id: "infw_education_books", name: "Czytanie & książki", priority: 3 },
          { id: "infw_education_studies", name: "Studia & uczelnia", priority: 3 },
        ],
      },
      {
        id: "infw_travel",
        name: "Podróże",
        emoji: "✈️",
        priority: 4,
        subcategories: [
          { id: "infw_travel_weekend", name: "Weekendy & city breaks", priority: 3 },
          { id: "infw_travel_budget", name: "Podróże budżetowe", priority: 3 },
          { id: "infw_travel_general", name: "Podróże ogólne", priority: 4 },
          { id: "infw_travel_kids", name: "Podróże z dziećmi", priority: 4 },
          { id: "infw_travel_solo", name: "Podróże solo & backpacking", priority: 4 },
          { id: "infw_travel_car", name: "Podróże autem", priority: 4 },
          { id: "infw_travel_luxury", name: "Luksusowe podróże", priority: 4 },
        ],
      },
      {
        id: "infw_pets",
        name: "Zwierzęta & Natura",
        emoji: "🐾",
        priority: 4,
        subcategories: [
          { id: "infw_pets_dogs", name: "Psy & opieka", priority: 2 },
          { id: "infw_pets_cats", name: "Koty & opieka", priority: 2 },
          { id: "infw_pets_training", name: "Szkolenie zwierząt", priority: 3 },
          { id: "infw_pets_garden", name: "Ogrodnictwo", priority: 3 },
          { id: "infw_pets_exotic", name: "Gryzonie & zwierzęta egzotyczne", priority: 4 },
          { id: "infw_pets_trekking", name: "Trekking & natura", priority: 4 },
        ],
      },
    ],
  },
  {
    id: "infm",
    name: "Influencer",
    emoji: "🧔",
    hint: "Tworzysz codzienny content na własny profil",
    categories: [
      {
        id: "infm_finance",
        name: "Finanse & Biznes",
        emoji: "💼",
        priority: 1,
        subcategories: [
          { id: "infm_finance_stocks", name: "Giełda & inwestowanie", priority: 1 },
          { id: "infm_finance_startup", name: "Przedsiębiorczość & startup", priority: 1 },
          { id: "infm_finance_branding", name: "Personal branding & social media", priority: 1 },
          { id: "infm_finance_sidehustle", name: "Zarabianie online & side hustle", priority: 1 },
          { id: "infm_finance_realestate", name: "Nieruchomości jako inwestycja", priority: 2 },
          { id: "infm_finance_passive", name: "Pasywny dochód", priority: 2 },
          { id: "infm_finance_sales", name: "Sprzedaż & negocjacje", priority: 2 },
          { id: "infm_finance_crypto", name: "Kryptowaluty", priority: 3 },
        ],
      },
      {
        id: "infm_health",
        name: "Zdrowie & Fitness",
        emoji: "💪",
        priority: 1,
        subcategories: [
          { id: "infm_health_strength", name: "Trening siłowy", priority: 1 },
          { id: "infm_health_diet", name: "Dietetyka, żywienie & suplementacja", priority: 1 },
          { id: "infm_health_functional", name: "Trening funkcjonalny & kalistenika", priority: 2 },
          { id: "infm_health_mma", name: "Sztuki walki & MMA", priority: 2 },
          { id: "infm_health_cardio", name: "Bieganie & cardio", priority: 2 },
          { id: "infm_health_mental", name: "Zdrowie psychiczne mężczyzn", priority: 2 },
          { id: "infm_health_biohacking", name: "Biohacking & regeneracja", priority: 3 },
        ],
      },
      {
        id: "infm_tech",
        name: "Tech & Gaming",
        emoji: "🎮",
        priority: 2,
        subcategories: [
          { id: "infm_tech_gaming", name: "Gaming & recenzje gier", priority: 1 },
          { id: "infm_tech_phones", name: "Smartfony & testy sprzętu", priority: 2 },
          { id: "infm_tech_ai", name: "AI tools & produktywność", priority: 2 },
          { id: "infm_tech_setup", name: "Gaming setup & gear", priority: 2 },
          { id: "infm_tech_pc", name: "Laptopy & PC buildy", priority: 3 },
          { id: "infm_tech_audio", name: "Audio & słuchawki", priority: 3 },
          { id: "infm_tech_drones", name: "Drony & foto tech", priority: 3 },
        ],
      },
      {
        id: "infm_education",
        name: "Edukacja & Rozwój",
        emoji: "🧠",
        priority: 2,
        subcategories: [
          { id: "infm_education_self", name: "Self-development & motywacja", priority: 1 },
          { id: "infm_education_psychology", name: "Psychologia & wpływ społeczny", priority: 1 },
          { id: "infm_education_productivity", name: "Produktywność & organizacja czasu", priority: 2 },
          { id: "infm_education_leadership", name: "Przywództwo & zarządzanie", priority: 2 },
          { id: "infm_education_debunking", name: "Debunking mitów & popularnonaukowe", priority: 2 },
          { id: "infm_education_history", name: "Historia & cywilizacje", priority: 3 },
          { id: "infm_education_books", name: "Książki & non-fiction", priority: 3 },
          { id: "infm_education_languages", name: "Nauka języków", priority: 3 },
        ],
      },
      {
        id: "infm_lifestyle",
        name: "Lifestyle & Rozrywka",
        emoji: "✨",
        priority: 3,
        subcategories: [
          { id: "infm_lifestyle_vlog", name: "Vlog & codzienność", priority: 2 },
          { id: "infm_lifestyle_humor", name: "Humor & memy", priority: 2 },
          { id: "infm_lifestyle_routine", name: "Morning routine & rytm dnia", priority: 2 },
          { id: "infm_lifestyle_music", name: "Muzyka & produkcja", priority: 3 },
          { id: "infm_lifestyle_extreme", name: "Sporty ekstremalne", priority: 3 },
          { id: "infm_lifestyle_series", name: "Seriale & filmy", priority: 3 },
        ],
      },
      {
        id: "infm_motor",
        name: "Motoryzacja & Sport",
        emoji: "🏎️",
        priority: 3,
        subcategories: [
          { id: "infm_motor_cars", name: "Motoryzacja & car lifestyle", priority: 2 },
          { id: "infm_motor_football", name: "Piłka nożna", priority: 2 },
          { id: "infm_motor_newcars", name: "Nowe modele & testy aut", priority: 2 },
          { id: "infm_motor_motorcycles", name: "Motocykle", priority: 3 },
          { id: "infm_motor_cycling", name: "Kolarstwo & MTB", priority: 3 },
          { id: "infm_motor_tuning", name: "Tuning & modyfikacje", priority: 3 },
          { id: "infm_motor_ev", name: "Elektryki & hybrydy", priority: 3 },
          { id: "infm_motor_basketball", name: "Koszykówka", priority: 4 },
        ],
      },
      {
        id: "infm_fashion",
        name: "Moda & Styl",
        emoji: "👔",
        priority: 3,
        subcategories: [
          { id: "infm_fashion_outfits", name: "Codzienne stylizacje & outfity", priority: 2 },
          { id: "infm_fashion_street", name: "Streetwear & hype", priority: 2 },
          { id: "infm_fashion_haul", name: "Haule & recenzje zakupów", priority: 2 },
          { id: "infm_fashion_sneakers", name: "Sneakery & limited drops", priority: 3 },
          { id: "infm_fashion_business", name: "Biznesowy styl", priority: 3 },
          { id: "infm_fashion_watches", name: "Zegarki & akcesoria", priority: 3 },
          { id: "infm_fashion_vintage", name: "Second-hand & vintage", priority: 4 },
        ],
      },
      {
        id: "infm_beauty",
        name: "Uroda & Beauty",
        emoji: "✂️",
        priority: 4,
        subcategories: [
          { id: "infm_beauty_hair", name: "Fryzury & stylizacja", priority: 2 },
          { id: "infm_beauty_beard", name: "Broda & zarost", priority: 2 },
          { id: "infm_beauty_skin", name: "Pielęgnacja skóry", priority: 3 },
          { id: "infm_beauty_tattoo", name: "Tatuaże & modyfikacje", priority: 3 },
          { id: "infm_beauty_hairloss", name: "Przeciwdziałanie łysieniu", priority: 3 },
          { id: "infm_beauty_perfume", name: "Perfumy & zapachy", priority: 4 },
        ],
      },
      {
        id: "infm_food",
        name: "Jedzenie & Gastronomia",
        emoji: "🍳",
        priority: 4,
        subcategories: [
          { id: "infm_food_restaurants", name: "Restauracje & food tour", priority: 2 },
          { id: "infm_food_recipes", name: "Przepisy & gotowanie", priority: 3 },
          { id: "infm_food_protein", name: "High protein recipes", priority: 3 },
          { id: "infm_food_world", name: "Kuchnia świata", priority: 4 },
          { id: "infm_food_coffee", name: "Kawa & espresso", priority: 4 },
        ],
      },
      {
        id: "infm_home",
        name: "Dom & Wnętrza",
        emoji: "🏡",
        priority: 4,
        subcategories: [
          { id: "infm_home_diy", name: "Narzędzia & DIY remonty", priority: 3 },
          { id: "infm_home_mancave", name: "Man cave & gaming room", priority: 2 },
          { id: "infm_home_smart", name: "Smart home", priority: 3 },
          { id: "infm_home_design", name: "Urządzanie & aranżacja wnętrz", priority: 4 },
          { id: "infm_home_office", name: "Home office setup", priority: 4 },
          { id: "infm_home_garden", name: "Ogród & taras", priority: 4 },
          { id: "infm_home_cleaning", name: "Sprzątanie & organizacja", priority: 4 },
        ],
      },
      {
        id: "infm_family",
        name: "Rodzina & Relacje",
        emoji: "👨‍👩‍👧",
        priority: 4,
        subcategories: [
          { id: "infm_family_fatherhood", name: "Ojcostwo & parenting", priority: 2 },
          { id: "infm_family_dating", name: "Randkowanie & dating", priority: 2 },
          { id: "infm_family_relationships", name: "Związki długoterminowe", priority: 3 },
          { id: "infm_family_friendship", name: "Przyjaźnie męskie", priority: 3 },
          { id: "infm_family_partner", name: "Życie z partnerką", priority: 3 },
          { id: "infm_family_divorce", name: "Rozwód & rozstanie", priority: 4 },
        ],
      },
      {
        id: "infm_travel",
        name: "Podróże",
        emoji: "✈️",
        priority: 4,
        subcategories: [
          { id: "infm_travel_workation", name: "Workation & digital nomad", priority: 2 },
          { id: "infm_travel_weekend", name: "Weekendy & city breaks", priority: 3 },
          { id: "infm_travel_solo", name: "Backpacking & podróże solo", priority: 3 },
          { id: "infm_travel_budget", name: "Podróże budżetowe", priority: 3 },
          { id: "infm_travel_general", name: "Podróże ogólne", priority: 4 },
          { id: "infm_travel_car", name: "Podróże autem", priority: 4 },
          { id: "infm_travel_luxury", name: "Luksusowe podróże", priority: 4 },
        ],
      },
      {
        id: "infm_pets",
        name: "Zwierzęta & Natura",
        emoji: "🐾",
        priority: 4,
        subcategories: [
          { id: "infm_pets_dogs", name: "Psy & opieka", priority: 2 },
          { id: "infm_pets_fishing", name: "Wędkarstwo", priority: 3 },
          { id: "infm_pets_survival", name: "Survival & bushcraft", priority: 3 },
          { id: "infm_pets_cats", name: "Koty & opieka", priority: 4 },
          { id: "infm_pets_exotic", name: "Zwierzęta egzotyczne & terraria", priority: 4 },
          { id: "infm_pets_training", name: "Szkolenie zwierząt", priority: 4 },
          { id: "infm_pets_trekking", name: "Trekking & natura", priority: 4 },
          { id: "infm_pets_garden", name: "Ogrodnictwo", priority: 4 },
        ],
      },
    ],
  },
  {
    id: "local",
    name: "Lokalny Biznes",
    emoji: "🏪",
    hint: "Prowadzisz fizyczny lokal lub usługę w okolicy",
    categories: [
      {
        id: "local_beauty",
        name: "Uroda & Beauty",
        emoji: "💇",
        priority: 1,
        subcategories: [
          { id: "local_beauty_hair_w", name: "Fryzjer damski", priority: 1 },
          { id: "local_beauty_barber", name: "Barber shop", priority: 1 },
          { id: "local_beauty_nails", name: "Manicure & paznokcie", priority: 1 },
          { id: "local_beauty_salon", name: "Salon kosmetyczny", priority: 1 },
          { id: "local_beauty_brows", name: "Salon brwi & rzęs", priority: 1 },
          { id: "local_beauty_aesthetic", name: "Medycyna estetyczna", priority: 1 },
          { id: "local_beauty_makeup", name: "Makijażystka", priority: 2 },
          { id: "local_beauty_tattoo", name: "Salon tatuażu", priority: 2 },
          { id: "local_beauty_spa", name: "Spa & masaż", priority: 2 },
          { id: "local_beauty_depil", name: "Depilacja & woskowanie", priority: 3 },
        ],
      },
      {
        id: "local_food",
        name: "Jedzenie & Gastronomia",
        emoji: "🍽️",
        priority: 1,
        subcategories: [
          { id: "local_food_cafe", name: "Kawiarnia & cukiernia", priority: 1 },
          { id: "local_food_restaurant", name: "Restauracja", priority: 1 },
          { id: "local_food_bakery", name: "Piekarnia & ciastkarnia", priority: 1 },
          { id: "local_food_truck", name: "Food truck", priority: 2 },
          { id: "local_food_pizza", name: "Pizzeria", priority: 2 },
          { id: "local_food_burger", name: "Burger & street food", priority: 2 },
          { id: "local_food_ice", name: "Lodziarnia", priority: 2 },
          { id: "local_food_asian", name: "Kuchnia azjatycka & sushi", priority: 3 },
          { id: "local_food_bar", name: "Bar & pub", priority: 3 },
          { id: "local_food_catering", name: "Catering", priority: 3 },
        ],
      },
      {
        id: "local_health",
        name: "Zdrowie & Fitness",
        emoji: "🏋️",
        priority: 1,
        subcategories: [
          { id: "local_health_pt", name: "Trener personalny", priority: 1 },
          { id: "local_health_pilates", name: "Studio pilates & yoga", priority: 1 },
          { id: "local_health_gym", name: "Siłownia & fitness club", priority: 1 },
          { id: "local_health_dietitian", name: "Dietetyk", priority: 1 },
          { id: "local_health_crossfit", name: "Studio crossfit & functional training", priority: 2 },
          { id: "local_health_psych", name: "Psycholog & terapeuta", priority: 2 },
          { id: "local_health_martial", name: "Szkoła sztuk walki", priority: 2 },
          { id: "local_health_dance", name: "Studio tańca", priority: 2 },
          { id: "local_health_physio", name: "Fizjoterapeuta", priority: 3 },
          { id: "local_health_running", name: "Klub biegowy", priority: 3 },
        ],
      },
      {
        id: "local_fashion",
        name: "Moda & Styl",
        emoji: "👗",
        priority: 2,
        subcategories: [
          { id: "local_fashion_w", name: "Butik odzieżowy damski", priority: 1 },
          { id: "local_fashion_vintage", name: "Second-hand & vintage sklep", priority: 1 },
          { id: "local_fashion_shoes", name: "Sklep z obuwiem", priority: 2 },
          { id: "local_fashion_m", name: "Butik odzieżowy męski", priority: 2 },
          { id: "local_fashion_acc", name: "Sklep z akcesoriami & torebkami", priority: 2 },
          { id: "local_fashion_jewelry", name: "Jubiler lokalny", priority: 3 },
          { id: "local_fashion_sport", name: "Sklep z odzieżą sportową", priority: 3 },
          { id: "local_fashion_lingerie", name: "Sklep z bielizną", priority: 3 },
          { id: "local_fashion_kids", name: "Sklep z odzieżą dziecięcą", priority: 3 },
          { id: "local_fashion_tailor", name: "Krawiec & usługi szwalnicze", priority: 4 },
        ],
      },
      {
        id: "local_lifestyle",
        name: "Lifestyle & Rozrywka",
        emoji: "🎉",
        priority: 2,
        subcategories: [
          { id: "local_lifestyle_florist", name: "Kwiaciarnia", priority: 1 },
          { id: "local_lifestyle_events", name: "Sala eventowa & organizacja imprez", priority: 2 },
          { id: "local_lifestyle_gifts", name: "Sklep z upominkami & prezentami", priority: 2 },
          { id: "local_lifestyle_boardgames", name: "Sklep z grami planszowymi", priority: 3 },
          { id: "local_lifestyle_wellness", name: "Klub fitness & wellness", priority: 3 },
          { id: "local_lifestyle_karaoke", name: "Punkt karaoke & rozrywka", priority: 3 },
          { id: "local_lifestyle_toys", name: "Sklep z zabawkami", priority: 3 },
          { id: "local_lifestyle_books", name: "Księgarnia lokalna", priority: 4 },
          { id: "local_lifestyle_cinema", name: "Kino & teatr lokalny", priority: 4 },
        ],
      },
      {
        id: "local_home",
        name: "Dom & Wnętrza",
        emoji: "🛋️",
        priority: 3,
        subcategories: [
          { id: "local_home_architect", name: "Architekt wnętrz & projektant", priority: 1 },
          { id: "local_home_renovation", name: "Firma remontowa & wykończeniowa", priority: 2 },
          { id: "local_home_decor", name: "Sklep z dekoracjami & dodatkami", priority: 2 },
          { id: "local_home_furniture", name: "Sklep z meblami", priority: 3 },
          { id: "local_home_garden", name: "Ogrodnictwo & sklep z roślinami", priority: 3 },
          { id: "local_home_textile", name: "Sklep z tekstyliami domowymi", priority: 3 },
          { id: "local_home_paint", name: "Sklep z farbami & tapetami", priority: 4 },
          { id: "local_home_lighting", name: "Sklep oświetleniowy", priority: 4 },
          { id: "local_home_tools", name: "Sklep z narzędziami & materiałami budowlanymi", priority: 4 },
          { id: "local_home_ceramics", name: "Sklep z ceramiką & wyrobami rzemieślniczymi", priority: 4 },
        ],
      },
      {
        id: "local_education",
        name: "Edukacja & Rozwój",
        emoji: "📚",
        priority: 3,
        subcategories: [
          { id: "local_education_languages", name: "Szkoła językowa", priority: 2 },
          { id: "local_education_courses", name: "Szkolenia zawodowe & coaching", priority: 2 },
          { id: "local_education_tutoring", name: "Korepetycje & nauczanie prywatne", priority: 3 },
          { id: "local_education_kindergarten", name: "Przedszkole & żłobek", priority: 3 },
          { id: "local_education_speech", name: "Logopeda", priority: 3 },
          { id: "local_education_music", name: "Szkoła muzyczna", priority: 4 },
          { id: "local_education_adults", name: "Centrum edukacji dorosłych", priority: 4 },
          { id: "local_education_art", name: "Szkoła artystyczna & plastyczna", priority: 4 },
        ],
      },
      {
        id: "local_family",
        name: "Rodzina & Relacje",
        emoji: "👶",
        priority: 3,
        subcategories: [
          { id: "local_family_photo", name: "Fotograf rodzinny & ślubny", priority: 1 },
          { id: "local_family_wedding", name: "Agencja ślubna & wedding planner", priority: 2 },
          { id: "local_family_play", name: "Sala zabaw", priority: 2 },
          { id: "local_family_animator", name: "Animator dzieci & eventy", priority: 3 },
          { id: "local_family_kids_shop", name: "Sklep z artykułami dla dzieci", priority: 3 },
          { id: "local_family_bridal", name: "Sklep & salon ślubny", priority: 3 },
          { id: "local_family_adoption", name: "Centrum adopcji & schronisko", priority: 4 },
        ],
      },
      {
        id: "local_motor",
        name: "Motoryzacja & Sport",
        emoji: "🚗",
        priority: 3,
        subcategories: [
          { id: "local_motor_detailing", name: "Myjnia & detailing", priority: 1 },
          { id: "local_motor_bike", name: "Sklep rowerowy", priority: 2 },
          { id: "local_motor_sport", name: "Sklep sportowy", priority: 2 },
          { id: "local_motor_workshop", name: "Warsztat samochodowy", priority: 3 },
          { id: "local_motor_bike_service", name: "Serwis rowerowy", priority: 3 },
          { id: "local_motor_club", name: "Klub sportowy & sekcja", priority: 3 },
          { id: "local_motor_rental", name: "Wypożyczalnia sprzętu sportowego", priority: 4 },
          { id: "local_motor_parts", name: "Sklep motoryzacyjny & części", priority: 4 },
          { id: "local_motor_fishing", name: "Sklep wędkarski", priority: 4 },
        ],
      },
      {
        id: "local_finance",
        name: "Finanse & Biznes",
        emoji: "💼",
        priority: 3,
        subcategories: [
          { id: "local_finance_marketing", name: "Agencja marketingowa", priority: 1 },
          { id: "local_finance_advisor", name: "Doradca finansowy", priority: 3 },
          { id: "local_finance_realestate", name: "Pośrednik nieruchomości", priority: 3 },
          { id: "local_finance_accounting", name: "Biuro rachunkowe", priority: 4 },
          { id: "local_finance_law", name: "Kancelaria prawna", priority: 4 },
          { id: "local_finance_insurance", name: "Agencja ubezpieczeń", priority: 4 },
          { id: "local_finance_credit", name: "Doradca kredytowy", priority: 4 },
          { id: "local_finance_hr", name: "Agencja rekrutacji & HR", priority: 4 },
        ],
      },
      {
        id: "local_tech",
        name: "Tech & Gaming",
        emoji: "💻",
        priority: 4,
        subcategories: [
          { id: "local_tech_studio", name: "Studio fotograficzne & wideo", priority: 2 },
          { id: "local_tech_print", name: "Drukarnia & studio graficzne", priority: 3 },
          { id: "local_tech_escape", name: "Escape room & VR", priority: 3 },
          { id: "local_tech_phones", name: "Serwis telefonów & laptopów", priority: 4 },
          { id: "local_tech_pc", name: "Sklep komputerowy", priority: 4 },
          { id: "local_tech_rtv", name: "Serwis RTV & AGD", priority: 4 },
          { id: "local_tech_games", name: "Sklep z grami & elektroniką", priority: 4 },
        ],
      },
      {
        id: "local_pets",
        name: "Zwierzęta & Natura",
        emoji: "🐾",
        priority: 4,
        subcategories: [
          { id: "local_pets_groomer", name: "Groomer & pielęgnacja zwierząt", priority: 2 },
          { id: "local_pets_training", name: "Szkolenie psów", priority: 2 },
          { id: "local_pets_vet", name: "Klinika weterynaryjna", priority: 3 },
          { id: "local_pets_hotel", name: "Hotel dla zwierząt", priority: 3 },
          { id: "local_pets_shop", name: "Sklep zoologiczny", priority: 3 },
          { id: "local_pets_walker", name: "Dogwalker & opieka nad zwierzętami", priority: 4 },
          { id: "local_pets_nursery", name: "Szkółka roślin & ogrodnictwo", priority: 4 },
          { id: "local_pets_farm", name: "Ferma & agroturystyka", priority: 4 },
        ],
      },
    ],
  },
  {
    id: "brand",
    name: "Marka / Firma",
    emoji: "🛍️",
    hint: "Sprzedajesz produkty lub usługi pod swoją marką",
    categories: [
      {
        id: "brand_beauty",
        name: "Uroda & Beauty",
        emoji: "💄",
        priority: 1,
        subcategories: [
          { id: "brand_beauty_skincare", name: "Pielęgnacja twarzy & skincare", priority: 1 },
          { id: "brand_beauty_makeup", name: "Marka makijażu", priority: 1 },
          { id: "brand_beauty_eco", name: "Naturalne & eko kosmetyki", priority: 1 },
          { id: "brand_beauty_body", name: "Pielęgnacja ciała", priority: 2 },
          { id: "brand_beauty_nails", name: "Marka paznokci & stylizacji", priority: 2 },
          { id: "brand_beauty_supplements", name: "Suplementy beauty", priority: 2 },
          { id: "brand_beauty_devices", name: "Narzędzia beauty & urządzenia", priority: 2 },
          { id: "brand_beauty_hair", name: "Pielęgnacja włosów", priority: 2 },
          { id: "brand_beauty_men", name: "Men grooming brand", priority: 3 },
          { id: "brand_beauty_perfume", name: "Perfumy & zapachy", priority: 3 },
        ],
      },
      {
        id: "brand_fashion",
        name: "Moda & Styl",
        emoji: "👕",
        priority: 1,
        subcategories: [
          { id: "brand_fashion_w", name: "Odzież damska", priority: 1 },
          { id: "brand_fashion_kids", name: "Odzież dziecięca", priority: 1 },
          { id: "brand_fashion_lingerie", name: "Bielizna & piżamy", priority: 2 },
          { id: "brand_fashion_acc", name: "Akcesoria & torebki", priority: 2 },
          { id: "brand_fashion_m", name: "Odzież męska", priority: 2 },
          { id: "brand_fashion_shoes", name: "Obuwie", priority: 2 },
          { id: "brand_fashion_sport", name: "Odzież sportowa & outdoor", priority: 3 },
          { id: "brand_fashion_jewelry", name: "Biżuteria & zegarki", priority: 3 },
        ],
      },
      {
        id: "brand_health",
        name: "Zdrowie & Fitness",
        emoji: "💊",
        priority: 1,
        subcategories: [
          { id: "brand_health_supplements", name: "Suplementy diety & odżywki", priority: 1 },
          { id: "brand_health_food", name: "Żywność zdrowotna & organic", priority: 1 },
          { id: "brand_health_herbs", name: "Medycyna naturalna & zioła", priority: 2 },
          { id: "brand_health_app", name: "Aplikacja zdrowotna & fitness", priority: 2 },
          { id: "brand_health_apparel", name: "Odzież sportowa (marka)", priority: 2 },
          { id: "brand_health_equipment", name: "Sprzęt fitness & siłowy", priority: 3 },
          { id: "brand_health_wellness", name: "Usługi wellness B2C", priority: 3 },
          { id: "brand_health_devices", name: "Urządzenia medyczne consumer", priority: 4 },
        ],
      },
      {
        id: "brand_food",
        name: "Jedzenie & Gastronomia",
        emoji: "🥤",
        priority: 2,
        subcategories: [
          { id: "brand_food_snacks", name: "Przekąski & słodycze", priority: 1 },
          { id: "brand_food_drinks", name: "Napoje funkcjonalne & energy", priority: 1 },
          { id: "brand_food_coffee", name: "Kawa & herbata", priority: 2 },
          { id: "brand_food_vegan", name: "Żywność wegańska & roślinna", priority: 2 },
          { id: "brand_food_d2c", name: "Własna marka food D2C", priority: 2 },
          { id: "brand_food_premium", name: "Produkty premium & gourmet", priority: 3 },
          { id: "brand_food_kids", name: "Żywność dla dzieci", priority: 3 },
          { id: "brand_food_catering", name: "Catering & meal kits", priority: 3 },
        ],
      },
      {
        id: "brand_lifestyle",
        name: "Lifestyle & Rozrywka",
        emoji: "🌿",
        priority: 2,
        subcategories: [
          { id: "brand_lifestyle_wellness", name: "Marka wellness lifestyle", priority: 1 },
          { id: "brand_lifestyle_box", name: "Subskrypcja box", priority: 2 },
          { id: "brand_lifestyle_candles", name: "Marka świec & aromaterapii", priority: 2 },
          { id: "brand_lifestyle_stationery", name: "Marka stacjonarnicza & planery", priority: 2 },
          { id: "brand_lifestyle_travel", name: "Marka podróżnicza & travel accessories", priority: 3 },
          { id: "brand_lifestyle_games", name: "Gry planszowe & zabawki", priority: 3 },
          { id: "brand_lifestyle_event", name: "Event brand & festiwal", priority: 3 },
          { id: "brand_lifestyle_hobby", name: "Platforma hobby", priority: 4 },
        ],
      },
      {
        id: "brand_home",
        name: "Dom & Wnętrza",
        emoji: "🛋️",
        priority: 3,
        subcategories: [
          { id: "brand_home_decor", name: "Dekoracje & dodatki", priority: 2 },
          { id: "brand_home_organizers", name: "Organizery & przechowywanie", priority: 2 },
          { id: "brand_home_textile", name: "Tekstylia domowe", priority: 3 },
          { id: "brand_home_appliances", name: "AGD małe & kuchenne", priority: 3 },
          { id: "brand_home_furniture", name: "Meble D2C", priority: 3 },
          { id: "brand_home_smart", name: "Smart home produkty", priority: 3 },
          { id: "brand_home_garden", name: "Ogród & balkon", priority: 3 },
          { id: "brand_home_eco", name: "Materiały eko & naturalne", priority: 4 },
          { id: "brand_home_lighting", name: "Oświetlenie", priority: 4 },
          { id: "brand_home_tools", name: "Narzędzia & DIY", priority: 4 },
        ],
      },
      {
        id: "brand_education",
        name: "Edukacja & Rozwój",
        emoji: "📚",
        priority: 3,
        subcategories: [
          { id: "brand_education_courses", name: "Platforma kursów online", priority: 1 },
          { id: "brand_education_coaching", name: "Coaching & mentoring (marka)", priority: 2 },
          { id: "brand_education_productivity", name: "Narzędzia do produktywności", priority: 2 },
          { id: "brand_education_planning", name: "Marka produktywności & planowania", priority: 2 },
          { id: "brand_education_languages", name: "Aplikacja do nauki języków", priority: 3 },
          { id: "brand_education_kids", name: "EdTech dla dzieci", priority: 3 },
          { id: "brand_education_publishing", name: "Wydawnictwo & e-booki", priority: 3 },
          { id: "brand_education_cert", name: "Certyfikacje & upskilling", priority: 4 },
        ],
      },
      {
        id: "brand_family",
        name: "Rodzina & Relacje",
        emoji: "🎁",
        priority: 3,
        subcategories: [
          { id: "brand_family_toys", name: "Zabawki & gry edukacyjne", priority: 2 },
          { id: "brand_family_baby", name: "Produkty dla niemowląt", priority: 2 },
          { id: "brand_family_kids_apparel", name: "Odzież dziecięca (marka)", priority: 2 },
          { id: "brand_family_edu_kids", name: "Marka edukacyjna dla dzieci", priority: 2 },
          { id: "brand_family_gifts", name: "Marka prezentowa", priority: 3 },
          { id: "brand_family_wedding", name: "Marka ślubna", priority: 3 },
          { id: "brand_family_parenting", name: "Platforma parentingowa", priority: 3 },
          { id: "brand_family_seniors", name: "Produkty dla seniorów", priority: 4 },
        ],
      },
      {
        id: "brand_tech",
        name: "Tech & Gaming",
        emoji: "📱",
        priority: 3,
        subcategories: [
          { id: "brand_tech_ai", name: "AI tools B2C", priority: 2 },
          { id: "brand_tech_app", name: "Aplikacja mobilna", priority: 2 },
          { id: "brand_tech_gadgets", name: "Sprzęt & gadżety tech", priority: 2 },
          { id: "brand_tech_wearables", name: "Wearables & smartwatch", priority: 3 },
          { id: "brand_tech_games", name: "Gry (studio & marka)", priority: 3 },
          { id: "brand_tech_audio", name: "Audio & akcesoria", priority: 3 },
          { id: "brand_tech_edutech", name: "EdTech platforma", priority: 3 },
          { id: "brand_tech_saas", name: "SaaS & oprogramowanie B2C", priority: 4 },
          { id: "brand_tech_video", name: "Foto & video sprzęt", priority: 4 },
        ],
      },
      {
        id: "brand_motor",
        name: "Motoryzacja & Sport",
        emoji: "🏃",
        priority: 4,
        subcategories: [
          { id: "brand_motor_apparel", name: "Odzież & akcesoria sportowe", priority: 2 },
          { id: "brand_motor_supplement", name: "Suplement sportowy", priority: 2 },
          { id: "brand_motor_app", name: "Aplikacja do sportu & treningu", priority: 2 },
          { id: "brand_motor_bike", name: "Marka rowerów & e-bike", priority: 3 },
          { id: "brand_motor_outdoor", name: "Sprzęt outdoor & survival", priority: 3 },
          { id: "brand_motor_car", name: "Marka akcesoriów auto", priority: 4 },
          { id: "brand_motor_winter", name: "Sprzęt do sportów zimowych", priority: 4 },
          { id: "brand_motor_fishing", name: "Marka wędkarska", priority: 4 },
          { id: "brand_motor_motor", name: "Marka motocyklowa & akcesoria", priority: 4 },
          { id: "brand_motor_water", name: "Sprzęt do sportów wodnych", priority: 4 },
        ],
      },
      {
        id: "brand_finance",
        name: "Finanse & Biznes",
        emoji: "💼",
        priority: 4,
        subcategories: [
          { id: "brand_finance_marketing", name: "Agencja marketingowa (sieć)", priority: 2 },
          { id: "brand_finance_consulting", name: "Consulting & doradztwo", priority: 3 },
          { id: "brand_finance_invest", name: "Platforma inwestycyjna", priority: 3 },
          { id: "brand_finance_fintech", name: "Fintech & neobank", priority: 4 },
          { id: "brand_finance_insurance", name: "Ubezpieczenia online", priority: 4 },
          { id: "brand_finance_b2b", name: "Oprogramowanie B2B", priority: 4 },
          { id: "brand_finance_hr", name: "HR tech", priority: 4 },
          { id: "brand_finance_legal", name: "LegalTech", priority: 4 },
        ],
      },
      {
        id: "brand_pets",
        name: "Zwierzęta & Natura",
        emoji: "🐾",
        priority: 4,
        subcategories: [
          { id: "brand_pets_food", name: "Karma premium dla zwierząt", priority: 2 },
          { id: "brand_pets_acc", name: "Akcesoria & gadżety pet", priority: 2 },
          { id: "brand_pets_health", name: "Zdrowie & suplementy dla zwierząt", priority: 3 },
          { id: "brand_pets_eco", name: "Marka eko & zrównoważona", priority: 3 },
          { id: "brand_pets_tech", name: "Pet tech & aplikacje", priority: 4 },
          { id: "brand_pets_apparel", name: "Odzież & akcesoria dla zwierząt", priority: 4 },
          { id: "brand_pets_garden", name: "Ogrodnictwo & rośliny e-com", priority: 4 },
          { id: "brand_pets_outdoor", name: "Marka outdoorowa & survival", priority: 4 },
        ],
      },
    ],
  },
];

// ───────────────────────────── Lookups ────────────────────────────────

const subcategoryIndex: Map<string, { segment: SegmentNode; category: CategoryNode; subcategory: SubcategoryNode }> = (() => {
  const map = new Map<string, { segment: SegmentNode; category: CategoryNode; subcategory: SubcategoryNode }>();
  for (const segment of segments) {
    for (const category of segment.categories) {
      for (const subcategory of category.subcategories) {
        map.set(subcategory.id, { segment, category, subcategory });
      }
    }
  }
  return map;
})();

export const getSegmentById = (id: string): SegmentNode | undefined =>
  segments.find((s) => s.id === id);

export const findSubcategory = (id: string) => subcategoryIndex.get(id);

export const isKnownSubcategoryId = (id: string): boolean => subcategoryIndex.has(id);

export const getCategoriesForSegment = (segmentId: string): CategoryNode[] => {
  const segment = getSegmentById(segmentId);
  if (!segment) return [];
  return [...segment.categories].sort((a, b) => a.priority - b.priority);
};

export const getSubcategoriesForCategory = (categoryId: string): SubcategoryNode[] => {
  for (const segment of segments) {
    const cat = segment.categories.find((c) => c.id === categoryId);
    if (cat) return [...cat.subcategories].sort((a, b) => a.priority - b.priority);
  }
  return [];
};

// ─────────────────────── Storage serialization ────────────────────────
//
// We store the user's selection in `user_profiles.category` as a JSON array
// of subcategory IDs. Single-string legacy values (from the old taxonomy)
// fail the array shape check and surface as `legacy = true` so the UI can
// prompt the user to re-pick.

export interface ParsedCategorySelection {
  subcategoryIds: string[];
  legacy: boolean;
  /** Original raw value from DB, useful for debug/telemetry. */
  raw: string | null;
}

export const serializeSelection = (subcategoryIds: string[]): string =>
  JSON.stringify(subcategoryIds);

export const parseStoredCategory = (raw: string | null | undefined): ParsedCategorySelection => {
  if (!raw) return { subcategoryIds: [], legacy: false, raw: raw ?? null };

  // Try JSON array first (new format).
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((item) => typeof item === "string")) {
      const known = parsed.filter(isKnownSubcategoryId);
      // Empty array or any unknown id ⇒ treat as legacy and ask user to re-pick.
      const legacy = parsed.length === 0 || known.length !== parsed.length;
      return { subcategoryIds: known, legacy, raw };
    }
  } catch {
    // Not JSON — fall through to legacy handling.
  }

  // Legacy single-string value — could be old "creator_other" or "creator/creator_other".
  return { subcategoryIds: [], legacy: true, raw };
};

/** Derives segment + category selection from saved subcategory leaves. */
export const deriveSelectionFromSubcategories = (subcategoryIds: string[]) => {
  const segmentIds = new Set<string>();
  const categoryIds = new Set<string>();
  for (const id of subcategoryIds) {
    const node = subcategoryIndex.get(id);
    if (!node) continue;
    segmentIds.add(node.segment.id);
    categoryIds.add(node.category.id);
  }
  const segmentId = segmentIds.size === 1 ? [...segmentIds][0] : null;
  return {
    segmentId,
    categoryIds: [...categoryIds],
    subcategoryIds,
  };
};

/** Human-readable summary, e.g. "Influencerka · Uroda & Beauty, Moda & Styl · 4 podkategorie". */
export const summarizeSelection = (subcategoryIds: string[]): string => {
  if (subcategoryIds.length === 0) return "";
  const { segmentId, categoryIds } = deriveSelectionFromSubcategories(subcategoryIds);
  const segment = segmentId ? getSegmentById(segmentId) : null;
  const categoryNames = categoryIds
    .map((cid) => {
      for (const seg of segments) {
        const c = seg.categories.find((cat) => cat.id === cid);
        if (c) return c.name;
      }
      return null;
    })
    .filter((n): n is string => Boolean(n));

  const parts: string[] = [];
  if (segment) parts.push(segment.name);
  if (categoryNames.length) parts.push(categoryNames.join(", "));
  parts.push(`${subcategoryIds.length} ${pluralize(subcategoryIds.length, ["podkategoria", "podkategorie", "podkategorii"])}`);
  return parts.join(" · ");
};

function pluralize(n: number, forms: [string, string, string]): string {
  if (n === 1) return forms[0];
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
}
